import axios from 'axios';
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';

/**
 * Classified risk when a farm has active alerts.
 * `null` means unclassified (no alerts) — not the same as low risk.
 */
export type FarmRiskLevel = 'high' | 'medium' | 'low';

export type FarmRiskReason = {
  source: string;
  label: string;
  severity: 'high' | 'medium' | 'low';
  message?: string | null;
  as_of?: string | null;
};

export type FarmRiskStatus = {
  farm_id: string;
  /** null when the farm has no active alerts and is not classified. */
  risk_level: FarmRiskLevel | null;
  alert_count: number;
  severity_counts: Record<string, number>;
  sources: string[];
  reasons: FarmRiskReason[];
};

type FarmRiskStatusResponse = {
  computed_at: string;
  items: FarmRiskStatus[];
};

/** Filter choices for the field list — only the three classified levels. */
export type FarmRiskFilter = 'all' | 'high' | 'medium' | 'low';

/** Matches the backend's per-request cap, so longer lists are sent in batches. */
const MAX_FARMS_PER_REQUEST = 1000;

function isRiskLevel(value: unknown): value is FarmRiskLevel {
  return value === 'high' || value === 'medium' || value === 'low';
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Risk status for a batch of farms, keyed by farm id.
 *
 * Returns an empty map rather than throwing: the field list must still render if the
 * risk service is unavailable, it just won't offer risk filtering.
 */
export async function fetchFarmRiskStatuses(
  farmIds: Array<number | string>
): Promise<Record<string, FarmRiskStatus>> {
  const ids = Array.from(
    new Set(farmIds.map((id) => String(id).trim()).filter((id) => id.length > 0))
  );
  if (ids.length === 0) return {};

  try {
    const responses = await Promise.all(
      chunk(ids, MAX_FARMS_PER_REQUEST).map((batch) =>
        axios.post<FarmRiskStatusResponse>(
          `${DIGISAKA_API_ORIGIN}/api/farm-risk-status/`,
          { farm_ids: batch },
          { withCredentials: true }
        )
      )
    );

    const byFarmId: Record<string, FarmRiskStatus> = {};
    for (const res of responses) {
      for (const item of res.data?.items ?? []) {
        if (!item?.farm_id) continue;
        byFarmId[String(item.farm_id)] = {
          ...item,
          risk_level: isRiskLevel(item.risk_level) ? item.risk_level : null,
          sources: Array.isArray(item.sources) ? item.sources : [],
          reasons: Array.isArray(item.reasons) ? item.reasons : [],
        };
      }
    }
    return byFarmId;
  } catch (e: unknown) {
    console.error('Farm risk status request failed:', e);
    return {};
  }
}

export function riskLevelOf(
  status: FarmRiskStatus | undefined | null
): FarmRiskLevel | null {
  return status?.risk_level ?? null;
}

export function matchesRiskFilter(
  level: FarmRiskLevel | null,
  filter: FarmRiskFilter
): boolean {
  if (filter === 'all') return true;
  // Unclassified farms (no alerts) never match a risk-level filter.
  return level === filter;
}

export const RISK_FILTER_OPTIONS: Array<{ value: FarmRiskFilter; label: string }> = [
  { value: 'all', label: 'Any risk' },
  { value: 'high', label: 'High risk' },
  { value: 'medium', label: 'Medium risk' },
  { value: 'low', label: 'Low risk' },
];

/**
 * Badge styling per level. No badge when unclassified.
 * Colours follow the alert cards in `AnalysisPanel`.
 */
export function riskBadgeMeta(
  level: FarmRiskLevel | null,
  isDark: boolean
): { label: string; className: string } | null {
  if (!level) return null;
  if (level === 'high') {
    return {
      label: 'High risk',
      className: isDark
        ? 'bg-red-500/20 text-red-200 border-red-500/40'
        : 'bg-red-50 text-red-700 border-red-200',
    };
  }
  if (level === 'medium') {
    return {
      label: 'Medium',
      className: isDark
        ? 'bg-orange-500/20 text-orange-200 border-orange-500/40'
        : 'bg-orange-50 text-orange-800 border-orange-200',
    };
  }
  return {
    label: 'Low',
    className: isDark
      ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
      : 'bg-amber-50 text-amber-800 border-amber-200',
  };
}

/**
 * Circular marker / map-pin chip classes by risk (sidebar number circles).
 * Unclassified keeps the default brand green.
 */
export function riskMarkerChipClass(
  level: FarmRiskLevel | null,
  isDark: boolean
): string {
  if (level === 'high') {
    return isDark
      ? 'bg-red-600/95 text-white ring-1 ring-red-400/40'
      : 'bg-red-600 text-white';
  }
  if (level === 'medium') {
    return isDark
      ? 'bg-orange-600/95 text-white ring-1 ring-orange-400/40'
      : 'bg-orange-600 text-white';
  }
  if (level === 'low') {
    return isDark
      ? 'bg-amber-600/95 text-white ring-1 ring-amber-400/40'
      : 'bg-amber-600 text-white';
  }
  return isDark ? 'bg-emerald-600/90 text-white' : 'bg-[#008540] text-white';
}

/** OpenLayers fill/stroke for farm polygons + circular pins by risk. */
export function riskMapSymbology(level: FarmRiskLevel | null | undefined): {
  pinFill: string;
  pinStroke: string;
  polyFillClose: string;
  polyFillFar: string;
  polyStrokeClose: string;
  polyStrokeFar: string;
} {
  if (level === 'high') {
    return {
      pinFill: '#dc2626',
      pinStroke: '#fef2f2',
      polyFillClose: 'rgba(220, 38, 38, 0.28)',
      polyFillFar: 'rgba(220, 38, 38, 0.10)',
      polyStrokeClose: '#dc2626',
      polyStrokeFar: 'rgba(220, 38, 38, 0.45)',
    };
  }
  if (level === 'medium') {
    return {
      pinFill: '#ea580c',
      pinStroke: '#fff7ed',
      polyFillClose: 'rgba(234, 88, 12, 0.26)',
      polyFillFar: 'rgba(234, 88, 12, 0.10)',
      polyStrokeClose: '#ea580c',
      polyStrokeFar: 'rgba(234, 88, 12, 0.45)',
    };
  }
  if (level === 'low') {
    return {
      pinFill: '#d97706',
      pinStroke: '#fffbeb',
      polyFillClose: 'rgba(217, 119, 6, 0.26)',
      polyFillFar: 'rgba(217, 119, 6, 0.10)',
      polyStrokeClose: '#d97706',
      polyStrokeFar: 'rgba(217, 119, 6, 0.45)',
    };
  }
  return {
    pinFill: '#0f766e',
    pinStroke: '#ecfdf5',
    polyFillClose: 'rgba(16, 185, 129, 0.22)',
    polyFillFar: 'rgba(16, 185, 129, 0.08)',
    polyStrokeClose: '#10B981',
    polyStrokeFar: 'rgba(16, 185, 129, 0.4)',
  };
}

/** Worst risk among a set (for map clusters). */
export function worstFarmRiskLevel(
  levels: Array<FarmRiskLevel | null | undefined>
): FarmRiskLevel | null {
  if (levels.some((l) => l === 'high')) return 'high';
  if (levels.some((l) => l === 'medium')) return 'medium';
  if (levels.some((l) => l === 'low')) return 'low';
  return null;
}

/** Short "why" text for the badge tooltip. */
export function riskSummaryText(status: FarmRiskStatus | undefined | null): string {
  if (!status || !status.risk_level) return 'No active alerts';
  const reasons = status.reasons.slice(0, 3).map((r) => r.label);
  const extra = status.alert_count - reasons.length;
  const list = reasons.join(', ');
  return extra > 0 ? `${list} +${extra} more` : list;
}
